import React, { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { pick, prop } from 'ramda';
import { debounce } from 'underscore';

import { InputShallow } from '@core/js/components/common/Fields/Input/components/InputShallow';
import { Icon } from '@core/js/components/common/Icon';

import { GameSearchResult } from '@interlayer/js/custom/components/main/GameSearchGlobal/components/GameSearchResult';

import { trackGameSearchChange } from '@core/js/components/main/Games/trackingApi';
import { useTranslations } from '@core/js/hooks/i18n';
import { useDeviceSelector, useGameConfigurationSelector } from '@core/js/store/hooks';
import { searchFlex } from '@core/js/utils/search.util';

import { useClickOutSide } from '@interlayer/js/custom/hooks/useClickOutSide';
import { useDynamicTagList } from '@interlayer/js/custom/hooks/useDynamicTagList';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/custom/utils/constants';
import { clsx } from '@interlayer/js/utils/classNames.util';

import { useAvailableGames } from './hooks/useAvailableGames';
import { DEFAULT_SEARCH_LIST, DELAY_GAMES_SEARCH } from './constants';
import { convertToRomanNumerals } from './helpers';
import { TGamesSearchGlobalProps, TSearchedGames } from './types';

import style from './GameSearchGlobal.scss';

export const GamesSearchGlobal: React.FC<TGamesSearchGlobalProps> = ({ onCloseSearch, isInitFocus }) => {
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const availableGames = useAvailableGames();
    const { isGamesLoaded, games } = useGameConfigurationSelector(pick(['isGamesLoaded', 'games']));
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [valueInput, setValueInput] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [searchList, setSearchList] = useState<TSearchedGames[]>(DEFAULT_SEARCH_LIST);
    const t = useTranslations('playtech.mobile.system.search.');
    const [dTagGameSearchMinCharacter] = useDynamicTagList({
        tagName: DYNAMIC_TAG_NAMES.GAME_SEARCH_MIN_CHARACTERS,
        isNumber: true,
        defaultValue: '3'
    });

    const callbackRef = useCallback((inputElement: HTMLInputElement) => {
        if (isInitFocus && inputElement) {
            inputElement.focus();
        }

        inputRef.current = inputElement;
    }, [isInitFocus]);

    const handleCloseSearch = useCallback(() => {
        setValueInput('');
        setIsActive(false);
        setSearchList(DEFAULT_SEARCH_LIST);

        if (onCloseSearch) {
            onCloseSearch();
        }
    }, [onCloseSearch]);

    useClickOutSide(wrapperRef, handleCloseSearch);

    const handleClickOnCloseIcon = useCallback(() => {
        setValueInput('');
        setSearchList(DEFAULT_SEARCH_LIST);

        inputRef.current?.focus();
    }, []);

    const searchGames = useMemo(
        () => searchFlex(availableGames),
        [availableGames]
    );

    const debounceSearch = useMemo(() => debounce((value: string) => {
        let searchedGames = searchGames(value);

        if (!searchedGames.length && /\d/.test(value)) {
            searchedGames = searchGames(convertToRomanNumerals(value));
        }

        setSearchList(searchedGames);
    }, DELAY_GAMES_SEARCH), [searchGames]);

    const handleChangeInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target?.value;
        const trimmedValue = value.trim();

        setValueInput(value);
        trackGameSearchChange('GamesSearch', null, trimmedValue);

        if (trimmedValue.length < dTagGameSearchMinCharacter) {
            setSearchList(DEFAULT_SEARCH_LIST);
            return;
        }

        debounceSearch(trimmedValue);
    }, [dTagGameSearchMinCharacter, debounceSearch]);

    const handleFocusInput = useCallback(() => {
        setIsActive(true);
    }, []);

    return (
        <div className={style.wrapper} ref={wrapperRef}>
            <div className={
                clsx(
                    style.inputWrapper,
                    {
                        [style.isActive]: isActive,
                        [style.isDisabled]: !isGamesLoaded || !games
                    }
                )}
            >
                {isDesktop
                    ? (
                        <Icon
                            type="search"
                            className={style.icon}
                        />
                    ) : (
                        <Icon
                            type="back_arrow"
                            className={style.iconBack}
                            onClick={handleCloseSearch}
                        />
                    )
                }

                <InputShallow
                    ref={callbackRef}
                    // @ts-expect-error: remove after InputShallow become ts file
                    value={valueInput}
                    autoComplete="off"
                    className={style.input}
                    placeholder={t('search')}
                    onChange={handleChangeInput}
                    onFocus={handleFocusInput}
                />

                <div className={style.inputActions}>
                    {valueInput && (
                        <>
                            {Boolean(searchList.length) && (
                                <div className={style.amount}>
                                    {searchList.length}

                                    {' '}

                                    {t(searchList.length > 1 ? 'items' : 'item')}
                                </div>
                            )}

                            {!isDesktop && (
                                <Icon
                                    // @ts-expect-error: remove after Icon become ts file
                                    tabIndex="-1"
                                    type="close_svg"
                                    onClick={handleClickOnCloseIcon}
                                    className={style.resetBtn}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {Boolean(searchList.length) && (
                <Suspense fallback="">
                    <GameSearchResult
                        searchList={searchList}
                        handlerCloseSearch={handleCloseSearch}
                    />
                </Suspense>
            )}
        </div>
    );
};
